import Router from "sap/ui/core/routing/Router";
import { GetApiAccessToken, IsUserAuthenticated } from "./AuthService";
import { Routes } from "../constants/Routes";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import {
  ApiParams,
  ApiGetArguments,
  ApiPostArguments,
} from "../models/ApiArguments";
import Dialog from "sap/m/Dialog";
import { ButtonType, DialogType } from "sap/m/library";
import { ValueState } from "sap/ui/core/library";
import Button from "sap/m/Button";
import Text from "sap/m/Text";
import { GetApiBaseUrl } from "../utils/ConfigUtil";

export default class ApiService {
  private baseUrl: string;
  private router: Router;
  private i18nModel: ResourceModel;
  private handleLoading: (isLoading: boolean) => void;
  public _oDialog: Dialog | null = null;
  private isUserAuthenticated: () => Promise<boolean>;

  public static async Create(
    router: Router,
    i18nModel: ResourceModel,
    handleLoading: (isLoading: boolean) => void,
    userAuthCheckFunction?: () => Promise<boolean>
  ): Promise<ApiService> {
    const apiService = new ApiService();

    apiService.baseUrl = await GetApiBaseUrl();
    apiService.router = router;
    apiService.i18nModel = i18nModel;
    apiService.handleLoading = handleLoading;
    // user auth check function is a parameter here so that we can have a shared function on a component level to prevent multiple token refresh requests at the same time
    apiService.isUserAuthenticated =
      userAuthCheckFunction || IsUserAuthenticated;

    return apiService;
  }

  private navToLogin(): void {
    const currentHash = this.router.getHashChanger().getHash();
    const routeInfo = this.router.getRouteInfoByHash(currentHash);
    if (routeInfo?.name == Routes.LOGIN) return;

    this.router.navTo(Routes.LOGIN, {
      returnRoute: routeInfo?.name,
      returnArgs:
        routeInfo?.arguments && Object.keys(routeInfo.arguments).length > 0
          ? encodeURI(JSON.stringify(routeInfo.arguments))
          : undefined,
    });
  }

  private buildUrlString(endpoint: string, params?: unknown): string {
    const url = new URL(this.baseUrl + endpoint);
    // We take params as "unknown" type and cast it here, because a "Record<>" type doesn't allow interfaces with statically defined properties.
    const paramsCast = params as ApiParams;

    for (const paramKey in paramsCast) {
      const paramValue = paramsCast[paramKey];
      if (paramValue) {
        if (Array.isArray(paramValue)) {
          for (const iterValue of paramValue) {
            url.searchParams.append(paramKey, "" + iterValue);
          }
        } else {
          url.searchParams.append(paramKey, "" + paramValue);
        }
      }
    }

    return url.toString();
  }

  public static getRequestHeaders(): HeadersInit | undefined {
    return {
      "Content-Type": "application/json",
      Authorization: "Bearer " + GetApiAccessToken(),
    };
  }

  public getBaseUrl = () => this.baseUrl;

  public async get(
    endpoint: string,
    getArgs?: ApiGetArguments
  ): Promise<Response> {
    if (getArgs?.checkToken !== false && !(await this.isUserAuthenticated())) {
      this.navToLogin();
      throw "User is unauthenticated";
    }

    const urlStr = this.buildUrlString(endpoint, getArgs?.params);

    this.setLoading(true);
    try {
      const response = await fetch(urlStr, {
        method: "GET",
        headers: ApiService.getRequestHeaders(),
      });
      this.setLoading(false);

      if (!response.ok && response.status == 401) {
        this.navToLogin();
      }

      return response;
    } catch (error) {
      this.setLoading(false);
      this.showExceptionError(endpoint);
      throw error;
    }
  }

  public async post(
    endpoint: string,
    postArgs?: ApiPostArguments
  ): Promise<Response> {
    if (postArgs?.checkToken !== false && !(await this.isUserAuthenticated())) {
      this.navToLogin();
      throw "User is unauthenticated";
    }

    const urlStr = this.buildUrlString(endpoint, postArgs?.params);
    let stringifiedBody;
    if (postArgs?.body) stringifiedBody = JSON.stringify(postArgs?.body);

    this.setLoading(true);
    try {
      let response: Response;
      if (stringifiedBody === undefined || stringifiedBody === null) {
        response = await fetch(urlStr, {
          method: "POST",
          headers: ApiService.getRequestHeaders(),
        });
      } else {
        response = await fetch(urlStr, {
          method: "POST",
          headers: ApiService.getRequestHeaders(),
          body: stringifiedBody,
        });
      }

      this.setLoading(false);

      if (!response.ok && response.status == 401) {
        this.navToLogin();
      }

      return response;
    } catch (error) {
      this.setLoading(false);
      this.showExceptionError(endpoint);
      throw error;
    }
  }

  private setLoading(value: boolean): void {
    if (this.handleLoading !== null && this.handleLoading !== undefined)
      this.handleLoading(value);
  }

  private async showExceptionError(endpoint: string): Promise<void> {
    const i18nResourceBundle = await this.i18nModel.getResourceBundle();

    const errorMsg = i18nResourceBundle.getText("fetchErrorMessage", [
      "",
      endpoint,
    ]) as string;

    const dialog = new Dialog({
      type: DialogType.Message,
      state: ValueState.Error,
      title: i18nResourceBundle.getText("error"),
      content: new Text({ text: errorMsg }),
      beginButton: new Button({
        type: ButtonType.Transparent,
        text: i18nResourceBundle.getText("close"),
        press: () => {
          dialog.close();
          dialog.destroy();
        },
      }),
    });

    dialog.open();
  }

  public async downloadFile(endpoint: string): Promise<void> {
    const response = await this.get(endpoint);

    // if (!response.ok) {
    // }

    const blob = await response.blob();
    const contentDisposition = response.headers.get("Content-Disposition");
    let fileName = "unknown.pdf";
    const match =
      contentDisposition &&
      contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match && match[1]) {
      fileName = match[1];
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.style.display = "none";

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}
